import React from 'react';
import { Tour } from '../../types/tours';
import { MapPin, Clock, Users, CalendarRange, UtensilsCrossed, TreePine } from 'lucide-react';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface BookingTourCardProps {
  tour: Tour;
  bookingData: {
    date: Date | null;
    groupType: 'group' | 'reduced' | 'private' | null;
    persons: number;
    totalPrice: number;
  };
}

const BookingTourCard: React.FC<BookingTourCardProps> = ({ tour, bookingData }) => {
  const [carouselImage, setCarouselImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchFirstCarouselImage = async () => {
      try {
        const { data, error } = await supabase
          .from('tour_images')
          .select('image_url')
          .eq('tour_id', tour.id)
          .eq('image_type', 'carousel')
          .eq('display_order', 0)
          .single();

        if (error) throw error;
        setCarouselImage(data?.image_url || null);
      } catch (err) {
        console.error('Error fetching carousel image:', err);
      }
    };

    fetchFirstCarouselImage();
  }, [tour.id]);

  return (
    <div className="bg-white rounded-xl overflow-hidden sticky top-32 hidden lg:block">
      {/* Image */}
      <div className="aspect-video relative">
        {/* Blurred background */}
        {carouselImage && (
          <div className="absolute inset-0">
            <img
              src={carouselImage}
              alt=""
              className="w-full h-full object-cover blur-md scale-110 opacity-90"
            />
          </div>
        )}
        {/* Main image with preserved aspect ratio */}
        {carouselImage && (
          <img
            src={carouselImage}
            alt={tour.name}
            className="absolute inset-0 w-full h-full object-contain"
          />
        )}
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        <h3 className="text-xl font-semibold text-[#2A382A]">{tour.name}</h3>

        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-3 text-[#2A382A]/70">
            <TreePine className="w-4 h-4" />
            <span>{tour.location}</span>
          </div>
          <div className="flex items-center gap-3 text-[#2A382A]/70">
            <Clock className="w-4 h-4" />
            <span>{tour.duration}</span>
          </div>
          <div className="flex items-center gap-3 text-[#2A382A]/70">
            <MapPin className="w-4 h-4" />
            <a 
              href={tour.meeting_point_url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#953B0E] transition-colors underline"
            >
              {tour.meeting_point}
            </a>
          </div>
          {bookingData.date && (
            <div className="flex items-center gap-3 text-[#2A382A]/70">
              <CalendarRange className="w-4 h-4" />
              <div>
                <div>{formatDate(bookingData.date, {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}</div>
                <div className="text-xs">
                  Starting at {formatTime(tour.departure).americanTime} ({formatTime(tour.departure).europeanTime})
                </div>
              </div>
            </div>
          )}
          {bookingData.persons > 0 && (
            <div className="flex items-center gap-3 text-[#2A382A]/70">
              <Users className="w-4 h-4" />
              <div>
                <span>{bookingData.persons} {bookingData.persons === 1 ? 'person' : 'persons'}</span>
                {bookingData.groupType && (
                  <div className="text-xs text-[#2A382A]/50">
                    ${bookingData.totalPrice / bookingData.persons} per person
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {bookingData.totalPrice > 0 && (
          <div className="pt-4 border-t border-[#2A382A]/10">
            <div className="flex justify-between items-center">
              <span className="text-[#2A382A]/70">Total Price</span>
              <span className="text-xl font-bold text-[#2A382A]">
                ${bookingData.totalPrice}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingTourCard;